import React, { forwardRef } from 'react';
import { cn } from './tokens';

// =============================================================================
// CARD VARIANTS
// =============================================================================

const variants = {
  default: `
    bg-slate-900 border border-slate-800
  `,
  elevated: `
    bg-slate-900 border border-slate-800
    shadow-lg shadow-black/20
  `,
  outlined: `
    bg-transparent border border-slate-700
  `,
  ghost: `
    bg-slate-900/50 border border-transparent
  `,
  interactive: `
    bg-slate-900 border border-slate-800
    hover:border-slate-700 hover:bg-slate-900/80
    cursor-pointer transition-all duration-200
  `,
  selected: `
    bg-slate-900 border-2 border-accent
    shadow-lg shadow-accent/10
  `,
} as const;

const paddings = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
} as const;

// =============================================================================
// TYPES
// =============================================================================

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual style variant */
  variant?: keyof typeof variants;
  /** Internal padding */
  padding?: keyof typeof paddings;
  /** Border radius size */
  radius?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether the card is in a selected state */
  isSelected?: boolean;
  /** Whether the card is disabled */
  isDisabled?: boolean;
}

// =============================================================================
// CARD COMPONENT
// =============================================================================

/**
 * Card component for containing related content
 *
 * @example
 * // Basic card
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Title</CardTitle>
 *     <CardDescription>Description text</CardDescription>
 *   </CardHeader>
 *   <CardContent>Content here</CardContent>
 * </Card>
 *
 * @example
 * // Interactive card
 * <Card variant="interactive" onClick={handleClick}>
 *   Clickable content
 * </Card>
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'none',
      radius = 'lg',
      isSelected = false,
      isDisabled = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const radiusMap = {
      sm: 'rounded-md',
      md: 'rounded-lg',
      lg: 'rounded-xl',
      xl: 'rounded-2xl',
    };

    return (
      <div
        ref={ref}
        className={cn(
          // Base styles
          'overflow-hidden',
          // Radius
          radiusMap[radius],
          // Variant (use selected variant if isSelected)
          isSelected ? variants.selected : variants[variant],
          // Padding
          paddings[padding],
          // Disabled state
          isDisabled && 'opacity-50 pointer-events-none',
          // Custom classes
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// =============================================================================
// CARD HEADER
// =============================================================================

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Add border below header */
  bordered?: boolean;
}

/**
 * Card header section for title and description
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ bordered = false, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-4 py-3',
          bordered && 'border-b border-slate-800',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// =============================================================================
// CARD TITLE
// =============================================================================

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * Card title text
 */
export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ as: Component = 'h3', className, children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(
          'text-lg font-bold text-slate-50',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardTitle.displayName = 'CardTitle';

// =============================================================================
// CARD DESCRIPTION
// =============================================================================

/**
 * Card description/subtitle text
 */
export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn(
        'text-sm text-slate-400 mt-1',
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
});

CardDescription.displayName = 'CardDescription';

// =============================================================================
// CARD CONTENT
// =============================================================================

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Remove default padding */
  noPadding?: boolean;
}

/**
 * Card main content area
 */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ noPadding = false, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          !noPadding && 'px-4 py-4',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

// =============================================================================
// CARD FOOTER
// =============================================================================

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Add border above footer */
  bordered?: boolean;
}

/**
 * Card footer section for actions
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ bordered = false, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-4 py-3 flex items-center gap-3',
          bordered && 'border-t border-slate-800 bg-slate-900/50',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// =============================================================================
// CARD IMAGE
// =============================================================================

interface CardImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Aspect ratio */
  aspectRatio?: 'square' | 'video' | 'wide' | 'auto';
  /** Object fit behavior */
  fit?: 'cover' | 'contain' | 'fill';
}

/**
 * Card image with consistent aspect ratio handling
 */
export const CardImage = forwardRef<HTMLImageElement, CardImageProps>(
  ({ aspectRatio = 'auto', fit = 'cover', className, alt, ...props }, ref) => {
    const aspectRatioMap = {
      square: 'aspect-square',
      video: 'aspect-video',
      wide: 'aspect-[2/1]',
      auto: '',
    };

    const fitMap = {
      cover: 'object-cover',
      contain: 'object-contain',
      fill: 'object-fill',
    };

    return (
      <div className={cn('overflow-hidden', aspectRatioMap[aspectRatio])}>
        <img
          ref={ref}
          alt={alt}
          className={cn(
            'w-full h-full',
            fitMap[fit],
            className
          )}
          {...props}
        />
      </div>
    );
  }
);

CardImage.displayName = 'CardImage';

export default Card;
